import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';

import { CloseIcon, StraightenIcon, DragPanIcon } from '@/assets/icons';
import { OverflowTypography } from '@common/components/OverflowTypography';
import { StyledTooltip } from '@/helpers/styles';

import { Mode } from '../../types';
import { Opacity } from './Opacity';

type Props = {
  referenceFileName: string;
  referenceMode: Mode;
  onScaleClick: () => void;
  onScaleCancelClick: () => void;
  onMoveClick: () => void;
  onMoveCancelClick: () => void;
  onMoveConfirmClick: () => void;
  isLoading: boolean;
  opacity: number;
  setOpacity: (value: number) => void;
  disabled: boolean;
};

export const ReferenceImageControls = ({
  referenceFileName,
  onScaleClick,
  onScaleCancelClick,
  referenceMode,
  onMoveClick,
  onMoveCancelClick,
  onMoveConfirmClick,
  isLoading,
  opacity,
  setOpacity,
  disabled,
}: Props) => {
  const { t } = useTranslation(['interface']);

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <Stack direction='row' alignItems='center' gap={1} sx={{ width: '100%' }}>
        {/* IMAGE ICON */}
        <CropOriginalIcon sx={{ width: '24px', height: '24px' }} />

        {/* IMAGE FILE NAME */}
        <OverflowTypography
          sx={{
            display: 'flex',
            fontSize: 12,
            mr: 'auto',
          }}
        >
          {referenceFileName}
        </OverflowTypography>

        {/* LOADING INDICATOR */}
        {isLoading && <CircularProgress size={18} />}

        <Stack direction='row' spacing={1}>
          {/* CANCEL MOVE BUTTON / MOVE BUTTON */}
          {referenceMode === 'move' ? (
            <StyledTooltip title='Cancel'>
              <IconButton onClick={onMoveCancelClick} sx={{ p: 0, width: '24px', height: '24px' }}>
                <CloseIcon />
              </IconButton>
            </StyledTooltip>
          ) : (
            <StyledTooltip
              title='Move reference image'
              disabled={disabled || referenceMode === 'scale'}
            >
              {/* Wrapping the child component to quench mui error "You provided a disabled button element as a child to a tooltip component." */}
              <span>
                <IconButton
                  disabled={disabled || referenceMode === 'scale'}
                  onMouseDown={onMoveClick}
                  sx={{ p: 0, width: '24px', height: '24px' }}
                >
                  <DragPanIcon size={18} />
                </IconButton>
              </span>
            </StyledTooltip>
          )}

          {/* CANCEL SCALE BUTTON / SCALE BUTTON */}
          {referenceMode === 'scale' ? (
            <StyledTooltip title='Cancel'>
              <IconButton onClick={onScaleCancelClick} sx={{ p: 0, width: '24px', height: '24px' }}>
                <CloseIcon />
              </IconButton>
            </StyledTooltip>
          ) : (
            <StyledTooltip title='Scale' disabled={disabled || referenceMode === 'move'}>
              {/* Wrapping the child component to quench mui error "You provided a disabled button element as a child to a tooltip component." */}
              <span>
                <IconButton
                  disabled={disabled || referenceMode === 'move'}
                  onClick={onScaleClick}
                  sx={{ p: 0, width: '24px', height: '24px' }}
                >
                  <StraightenIcon />
                </IconButton>
              </span>
            </StyledTooltip>
          )}
        </Stack>
      </Stack>

      {/* FINISH MOVING BUTTON */}
      {referenceMode === 'move' && (
        <Button onClick={onMoveConfirmClick} type='submit'>
          {t('reference.properties.finish_moving_image')}
        </Button>
      )}

      {/* TRANSPARENCY */}
      {!(opacity || opacity === 0) ? null : (
        <Opacity value={opacity} setValue={setOpacity} disabled={disabled} />
      )}
    </Stack>
  );
};
